import { ChevronIcon } from 'components/icons'
import { generateContext } from 'context/generate-context'
import classNames from 'helpers/class-names'
import Head from 'next/head'
import Link from 'next/link'
import { FC, ReactNode } from 'react'

type Breakpoints = 'sm' | 'md' | 'lg' | 'xl'

type BreadcrumbContext = {
  breakpoint?: Breakpoints
}

export const [_BreadcrumbProvider, useBreadcrumb] =
  generateContext<BreadcrumbContext>({ breakpoint: 'md' })

type BreadcrumbProviderProps = {
  chidren?: ReactNode
  breakpoint?: Breakpoints
}

export const BreadcrumbProvider: FC<BreadcrumbProviderProps> = ({
  children,
  breakpoint = 'md',
}) => {
  return (
    <_BreadcrumbProvider value={{ breakpoint }}>{children}</_BreadcrumbProvider>
  )
}

interface ItemProps {
  to: string
  children: ReactNode
  current?: boolean
  previous?: boolean
  breakpoint?: 'sm' | 'md' | 'lg' | 'xl'
}

interface ListProps {
  children: ReactNode
  breakpoint?: Breakpoints
}

const Item: FC<ItemProps> = ({
  to,
  children,
  current = false,
  previous = false,
}) => {
  const { breakpoint } = useBreadcrumb()

  return (
    <li
      className={classNames(`hidden items-center ${breakpoint}:flex`, {
        flex: previous,
        hidden: !previous,
      })}>
      {current ? (
        <>
          <Head>
            <title>{children} - Toolpath</title>
          </Head>
          <span className="flex truncate pl-2 text-gray-300">{children}</span>
        </>
      ) : (
        <>
          <Link href={to}>
            <a
              className="flex items-center rounded px-2 text-gray-300
              focus:outline-none focus-visible:ring-2 focus-visible:ring-info/75">
              {previous && (
                <ChevronIcon
                  className={`flex size-5 rotate-180 truncate text-gray-300 ${breakpoint}:hidden`}
                />
              )}
              {children}
            </a>
          </Link>
          <ChevronIcon
            className={`hidden size-5 text-gray-300 ${breakpoint}:flex`}
          />
        </>
      )}
    </li>
  )
}

const List: FC<ListProps> = ({ children, breakpoint = 'md' }) => (
  <BreadcrumbProvider breakpoint={breakpoint}>
    <ul className="flex whitespace-nowrap text-sm font-normal tracking-wide text-gray-300 ">
      {children}
    </ul>
  </BreadcrumbProvider>
)

export const Breadcrumbs = { List, Item }
